import { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import { NoUpcomingEvents } from "@/react/components/Events/NewEventsSpace/ListView/NoUpcomingEvents";
import { ListViewItem } from "@/react/components/Events/NewEventsSpace/ListViewItem";
import { useCreateEvent } from "@circle-react/components/Events/NewEventButton/useCreateEvent";
import { usePunditUserContext } from "@circle-react/contexts";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { EmptyState } from "@circle-react-uikit/EmptyState";
import { Loader } from "@circle-react-uikit/Loader";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { EventsTab } from "./EventsTab";
import { UpcomingEvents } from "./UpcomingEvents";
import { EVENT_TYPES } from "./constants";
import { EVENT_LAYOUT_TYPES } from "./constants";
import { useAllEvents } from "./hooks/useAllEvents";
import { useHasEvents } from "./hooks/useHasEvents";

export interface EventsListProps {
  activeView: string;
}

export const EventsList = ({ activeView }: EventsListProps) => {
  const [activeTab, setActiveTab] = useState(EVENT_TYPES.UPCOMING);
  const isUpcomingEvents = activeTab === EVENT_TYPES.UPCOMING;
  const isPastEvents = activeTab === EVENT_TYPES.PAST;
  const isThumbnailView = activeView === EVENT_LAYOUT_TYPES.THUMBNAIL;

  const { hasEvents, isLoading: isLoadingHasEvents } = useHasEvents();
  const { showCreateEventModal } = useCreateEvent();
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const {
    events,
    isLoading: isLoadingEvents,
    fetchNextPage,
    hasNextPage,
    perPageEventIds,
    onEventDestroy,
    onEventChange,
  } = useAllEvents({
    params: { past_events: isPastEvents },
  });

  if (isLoadingHasEvents) {
    return <Loader />;
  }

  if (!hasEvents && isAdmin) {
    return (
      <EmptyState
        header={t("events.no_events.title")}
        description={t("events.no_events.description")}
        primaryLabel={t("events.no_events.cta")}
        onPrimaryClick={showCreateEventModal}
      />
    );
  }

  if (!hasEvents && !isAdmin) {
    return (
      <EmptyState
        header={t("events.no_events_user.title")}
        description={t("events.no_events_user.description")}
      />
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-0">
      <EventsTab activeTab={activeTab} setActiveTab={setActiveTab} />
      {isLoadingEvents && (
        <SkeletonLoader
          variant="new-events-list-view"
          disableCoverImage={!isUpcomingEvents}
        />
      )}
      {events.length === 0 && !isLoadingEvents && (
        <NoUpcomingEvents isPastEvents={isPastEvents} isDraft={false} />
      )}
      <PostsDetailsContextProvider perPagePostIds={perPageEventIds}>
        <InfiniteScroll
          style={{ overflow: "initial" }}
          scrollThreshold={0.5}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={<LoaderRow />}
          dataLength={events?.length || 0}
          scrollableTarget="panel"
          className="pb-6"
        >
          {isUpcomingEvents ? (
            <UpcomingEvents
              events={events}
              onEventDestroy={onEventDestroy}
              onEventChange={onEventChange}
              isThumbnailView={isThumbnailView}
            />
          ) : (
            <div
              className={classNames(
                "border-primary bg-primary sm:rounded-2xl",
                {
                  "border-y sm:border": Boolean(events.length),
                },
              )}
            >
              {events.map((event: any) => (
                <ListViewItem
                  key={event.id}
                  event={event}
                  onDestroy={() => onEventDestroy(event.id)}
                  onRsvp={() => onEventChange(event)}
                  isThumbnailView={isThumbnailView}
                />
              ))}
            </div>
          )}
        </InfiniteScroll>
      </PostsDetailsContextProvider>
    </div>
  );
};
